import { useState, useEffect } from 'react'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { ExternalLink, Loader2, Send, Copy, Check, ArrowRight } from 'lucide-react'
import { toast } from '@/components/ui/sonner'
import { Link } from 'react-router-dom'
import athleteApi from '@/lib/api'

interface PaymentInfoCardProps {
  campaign: any
  campaignId: string
  containerInvokeClass?: string
}

interface InvoiceStatus {
  invoiced: boolean
  invoice_id?: string
  status?: string
  amount_due?: number
  hosted_invoice_url?: string
  due_date?: number | null
  sent_at?: string | null
}

const PaymentInfoCard = ({ campaign, campaignId, containerInvokeClass }: PaymentInfoCardProps) => {
  const [invoiceStatus, setInvoiceStatus] = useState<InvoiceStatus | null>(null)
  const [loadingStatus, setLoadingStatus] = useState(false)
  const [isSending, setIsSending] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [recipientEmail, setRecipientEmail] = useState('')
  const [copied, setCopied] = useState(false)
  const [needsStripeConnect, setNeedsStripeConnect] = useState(false)

  const paymentType: 'fixed' | 'custom' = campaign.paymentType ?? 'fixed'
  const isCustom = paymentType === 'custom'
  const hasPaymentAmount = !isCustom && campaign.paymentAmount && campaign.paymentAmount > 0
  const alreadyPaid = campaign.paymentStatus === 'paid'

  useEffect(() => {
    if (!hasPaymentAmount || alreadyPaid || !campaignId) return
    setLoadingStatus(true)
    athleteApi
      .get(`/athlete/campaigns/${campaignId}/invoice-status`)
      .then((res) => setInvoiceStatus(res.data))
      .catch(() => setInvoiceStatus({ invoiced: false }))
      .finally(() => setLoadingStatus(false))
  }, [campaignId, hasPaymentAmount, alreadyPaid])

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount)

  const formatDate = (dateString: string | number) => {
    const date =
      typeof dateString === 'number' ? new Date(dateString * 1000) : new Date(dateString)
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
  }

  const handleSendInvoice = async () => {
    if (!recipientEmail.trim() || !campaignId) return
    setIsSending(true)
    try {
      const res = await athleteApi.post(`/athlete/campaigns/${campaignId}/send-invoice`, {
        recipientEmail: recipientEmail.trim(),
      })
      setInvoiceStatus({
        invoiced: true,
        invoice_id: res.data.invoice_id,
        status: res.data.status,
        amount_due: res.data.amount_due,
        hosted_invoice_url: res.data.invoice_url,
        sent_at: new Date().toISOString(),
      })
      setShowModal(false)
      setRecipientEmail('')
      toast('Invoice sent', {
        description: `Stripe has emailed the invoice to ${recipientEmail.trim()}. Share the payment link below with anyone who needs to pay.`,
      })
    } catch (error: any) {
      const data = error.response?.data
      if (data?.needs_stripe_connect) {
        setNeedsStripeConnect(true)
        setShowModal(false)
      }
      const msg = data?.error || 'Failed to send invoice. Please try again.'
      toast.error('Error', { description: msg })
    } finally {
      setIsSending(false)
    }
  }

  const handleCopyLink = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
      toast('Copied', { description: 'Payment link copied to clipboard.' })
    } catch {
      toast.error('Could not copy to clipboard')
    }
  }

  const renderStatus = () => {
    if (isCustom) {
      return (
        <span className="text-gray-700">
          {campaign.customPaymentTerms || 'Custom terms agreed off-platform'}
        </span>
      )
    }

    if (alreadyPaid) {
      return (
        <Badge variant="secondary" className="bg-gray-800 text-green-400 hover:bg-gray-700">
          Paid
        </Badge>
      )
    }

    if (!hasPaymentAmount) {
      return <span className="text-gray-600">To be paid upon completion</span>
    }

    if (loadingStatus) {
      return (
        <span className="flex items-center gap-2 text-gray-600 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading...
        </span>
      )
    }

    // Stripe setup required
    if (needsStripeConnect) {
      return (
        <div className="space-y-2">
          <p className="text-sm text-orange-700 font-medium">Stripe account required</p>
          <p className="text-xs text-gray-600">
            Connect a Stripe account to send invoices and receive payments.
          </p>
          <Link
            to="/earnings?tab=setup"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-800 underline hover:text-black"
          >
            Set up payment account
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      )
    }

    // Invoice already sent
    if (invoiceStatus?.invoiced) {
      const url = invoiceStatus.hosted_invoice_url
      return (
        <div className="space-y-2">
          <Badge variant="secondary" className="bg-blue-800/80 text-blue-200">
            Invoice Sent
          </Badge>
          {invoiceStatus.sent_at && (
            <p className="text-sm text-gray-600">Sent {formatDate(invoiceStatus.sent_at)}</p>
          )}
          {invoiceStatus.due_date && (
            <p className="text-sm text-gray-600">Due {formatDate(invoiceStatus.due_date)}</p>
          )}
          {url && (
            <div className="flex items-center gap-2 pt-1">
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm text-gray-700 underline hover:text-gray-900"
              >
                View Invoice
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
              <button
                onClick={() => handleCopyLink(url)}
                className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 border border-gray-300 rounded px-2 py-0.5"
              >
                {copied ? (
                  <>
                    <Check className="h-3.5 w-3.5 text-green-600" />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy className="h-3.5 w-3.5" />
                    Copy link
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      )
    }

    // Ready to invoice
    return (
      <div className="space-y-2">
        <span className="text-orange-600 font-medium">Pending payment</span>
        <div>
          <button
            onClick={() => setShowModal(true)}
            className="mt-1 inline-flex items-center gap-2 px-4 py-2 bg-gray-800 text-white text-sm rounded-lg hover:bg-gray-700"
          >
            <Send className="h-4 w-4" />
            Send Invoice
          </button>
          <p className="mt-2 text-xs text-gray-500">
            Creates a Stripe invoice. You'll get a payment link to share with anyone.
          </p>
        </div>
      </div>
    )
  }

  return (
    <>
      <Card
        className={`${containerInvokeClass} bg-[#AFEEF9] shadow-sm rounded-3xl rounded-bl-none border border-white/5`}
      >
        <CardHeader>
          <CardTitle className="text-gray-800">Payment Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-medium text-gray-600 mb-1">
                {isCustom ? 'PAYMENT TERMS' : 'AMOUNT'}
              </h3>
              {isCustom ? (
                <p className="text-base text-gray-800 font-medium leading-snug">
                  {campaign.customPaymentTerms || '—'}
                </p>
              ) : (
                <p className="text-4xl font-light text-gray-800">
                  {hasPaymentAmount ? formatCurrency(campaign.paymentAmount) : '—'}
                </p>
              )}
            </div>

            {!isCustom && (
              <div>
                <h3 className="text-sm font-medium text-gray-600 mb-1">STATUS</h3>
                {renderStatus()}
              </div>
            )}

            {!isCustom && alreadyPaid && campaign.paymentDate && (
              <div>
                <h3 className="text-sm font-medium text-gray-600 mb-1">PAYMENT DATE</h3>
                <p className="text-gray-700">{formatDate(campaign.paymentDate)}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-md rounded-2xl border border-white/10 bg-[#0d1b24] p-6">
            <h2 className="text-lg font-semibold text-white mb-1">Send Stripe invoice</h2>
            <p className="text-sm text-gray-400 mb-4">
              Enter the billing email for the brand or marketer. Stripe will email them the invoice
              and you'll get a shareable payment link.
            </p>
            <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">
              Recipient email
            </label>
            <input
              type="email"
              value={recipientEmail}
              onChange={(e) => setRecipientEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSendInvoice()}
              placeholder="billing@brand.com"
              className="w-full rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-sm text-white outline-hidden focus:border-[#C0FD72]/40 mb-5"
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowModal(false)
                  setRecipientEmail('')
                }}
                className="px-4 py-2 text-sm text-gray-300 border border-white/15 rounded-lg hover:bg-white/10 bg-white/5"
              >
                Cancel
              </button>
              <button
                onClick={handleSendInvoice}
                disabled={!recipientEmail.trim() || isSending}
                className="px-4 py-2 text-sm font-semibold bg-[#C0FD72] text-black rounded-lg hover:bg-[#C0FD72]/90 disabled:opacity-50 flex items-center gap-2"
              >
                {isSending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Sending…
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4" />
                    Send invoice
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default PaymentInfoCard
